import React from "react";
import LegalLayout from "../components/LegalLayout";

const P = ({ children }) => (
  <p className="mb-4 text-slate-700">{children}</p>
);
const UL = ({ children }) => (
  <ul className="mb-4 list-disc space-y-1.5 pl-6 text-slate-700">{children}</ul>
);

const sections = [
  {
    id: "intro",
    heading: "Our Commitment",
    content: (
      <P>
        Security is foundational to the trust our customers — and the
        consumers in our database — place in us. Below is a summary of how we
        protect data across our platform.
      </P>
    ),
  },
  {
    id: "data-protection",
    heading: "Data Protection",
    content: (
      <UL>
        <li>
          <strong>Encryption in Transit.</strong> All traffic to and from the
          BuyerBeacon Pro platform is encrypted using TLS 1.2 or higher with
          modern cipher suites.
        </li>
        <li>
          <strong>Encryption at Rest.</strong> Production data stores are
          encrypted at rest using AES-256.
        </li>
        <li>
          <strong>Hashing &amp; Truncation.</strong> Where feasible,
          identifiers (e.g., email addresses) are hashed (SHA-256) or
          truncated before storage and transmission.
        </li>
        <li>
          <strong>Tokenization.</strong> Sensitive customer credentials and
          API keys are tokenized and stored in a secrets manager separate from
          application data.
        </li>
      </UL>
    ),
  },
  {
    id: "infrastructure",
    heading: "Infrastructure",
    content: (
      <UL>
        <li>
          <strong>Hosting.</strong> Production infrastructure runs on tier-1
          cloud providers (AWS, GCP) located in the United States, in
          facilities with SOC 2 / ISO 27001-aligned controls.
        </li>
        <li>
          <strong>Network Segmentation.</strong> Production, staging, and
          corporate networks are isolated. Access to production is restricted
          to a small group of vetted personnel via VPN with multi-factor
          authentication.
        </li>
        <li>
          <strong>Web Application Firewall.</strong> Edge traffic is filtered
          through a managed WAF with DDoS mitigation, bot management, and
          rate-limiting.
        </li>
        <li>
          <strong>Backups.</strong> Encrypted, versioned, off-site backups with
          documented restoration runbooks.
        </li>
      </UL>
    ),
  },
  {
    id: "identity-access",
    heading: "Identity & Access",
    content: (
      <UL>
        <li>
          <strong>Least Privilege.</strong> Access to systems and data follows
          the principle of least privilege, with quarterly access reviews.
        </li>
        <li>
          <strong>MFA.</strong> Multi-factor authentication is required for all
          employee accounts and any administrative system access.
        </li>
        <li>
          <strong>SSO.</strong> Administrative access to internal systems is
          brokered through SSO with audit logging.
        </li>
        <li>
          <strong>Offboarding.</strong> Departing personnel are deprovisioned
          within one (1) business day.
        </li>
      </UL>
    ),
  },
  {
    id: "appsec",
    heading: "Application Security",
    content: (
      <UL>
        <li>
          <strong>Secure Development Lifecycle (SDLC).</strong> Code is
          peer-reviewed, scanned with static analysis tools, and tested before
          release.
        </li>
        <li>
          <strong>Dependency Management.</strong> Open-source dependencies are
          scanned for known vulnerabilities (CVE) and patched on a defined
          cadence.
        </li>
        <li>
          <strong>Penetration Testing.</strong> Annual third-party penetration
          testing of customer-facing surfaces.
        </li>
        <li>
          <strong>Vulnerability Management.</strong> Critical vulnerabilities
          are remediated within 7 days of disclosure; high-severity within 30
          days.
        </li>
      </UL>
    ),
  },
  {
    id: "monitoring",
    heading: "Monitoring & Incident Response",
    content: (
      <UL>
        <li>
          <strong>Audit Logging.</strong> Administrative access and sensitive
          data operations are logged and retained for at least 12 months.
        </li>
        <li>
          <strong>24/7 Monitoring.</strong> Anomalous activity in production
          triggers automated alerts to the on-call engineer.
        </li>
        <li>
          <strong>Incident Response Plan.</strong> We maintain a documented
          incident-response playbook with defined roles, communication paths,
          and post-mortem requirements.
        </li>
        <li>
          <strong>Customer Notification.</strong> In the event of a confirmed
          security incident affecting customer or consumer data, we notify
          affected customers without undue delay, as required by applicable
          law.
        </li>
      </UL>
    ),
  },
  {
    id: "people",
    heading: "People & Process",
    content: (
      <UL>
        <li>
          <strong>Background Checks.</strong> Background screening for
          personnel with access to production systems.
        </li>
        <li>
          <strong>Security Training.</strong> Annual security and privacy
          training for all employees and contractors with access to customer
          or consumer data.
        </li>
        <li>
          <strong>Confidentiality.</strong> All personnel and contractors are
          bound by written confidentiality obligations.
        </li>
        <li>
          <strong>Vendor Diligence.</strong> Sub-processors are reviewed for
          security posture before onboarding and audited periodically.
        </li>
      </UL>
    ),
  },
  {
    id: "compliance-alignment",
    heading: "Compliance Alignment",
    content: (
      <>
        <P>
          While certifications evolve, our internal controls are designed to
          align with the following frameworks:
        </P>
        <UL>
          <li>SOC 2 Type II (in progress)</li>
          <li>ISO/IEC 27001 (target)</li>
          <li>NIST Cybersecurity Framework</li>
          <li>GDPR Article 32 (security of processing)</li>
          <li>CCPA / CPRA security obligations</li>
        </UL>
      </>
    ),
  },
  {
    id: "disclosure",
    heading: "Responsible Disclosure",
    content: (
      <>
        <P>
          We welcome reports from security researchers. If you believe you've
          identified a vulnerability in BuyerBeacon Pro:
        </P>
        <UL>
          <li>
            Email{" "}
            <a
              href="mailto:admin@buyerbeacon.pro"
              className="text-[#1e1b4b] underline-offset-4 hover:underline"
            >
              admin@buyerbeacon.pro
            </a>{" "}
            with the subject "Security Disclosure"
          </li>
          <li>
            Provide reproduction steps, impact assessment, and any
            proof-of-concept
          </li>
          <li>
            Allow us a reasonable window (typically 30–90 days) before public
            disclosure
          </li>
        </UL>
        <P>
          We will respond within five (5) business days and work in good faith
          toward remediation. We do not pursue legal action against researchers
          who follow this responsible disclosure process in good faith.
        </P>
      </>
    ),
  },
];

export default function SecurityPage() {
  return (
    <LegalLayout
      eyebrow="Trust"
      title="Security"
      lastUpdated="February 2026"
      path="/security"
      seoDescription="BuyerBeacon Pro security — TLS 1.2+, AES-256 encryption, MFA, WAF, annual pen testing, SOC 2 in progress, responsible disclosure program."
      sections={sections}
    />
  );
}
